import { useNavigate } from "react-router-dom";
import { usePermissions, useReferralDashboard, useToasts } from "../../../../contexts";
import { Button, Col, Row, Table } from "react-bootstrap";
import { Claims, ReferralImport } from "../../../../models";
import { FormCard, Icon, LoadingSpinner, fas } from "../../../../component-library";
import { ReferralRow, ReferralHbovRow, CallBackRow } from ".";
import { Lists } from "../../../lists";
import { useCaseQuery } from "../../../../queries";
import { ReferralImportRow } from "./ReferralImportRow";
import { ReferralImportModal } from "./ReferralImportModal";
import { useState } from "react";

export function ReferralDashboardNew() {
    const navigate = useNavigate();
    const { hasPermission } = usePermissions();
    const { createMutation } = useCaseQuery();
    const { isLoading, dashboardData, handleShowCallBackModal } = useReferralDashboard();
    const { errorToast } = useToasts();

    const newReferralTitle = `New Referrals ${dashboardData?.newReferrals?.length ? `(${dashboardData.newReferrals.length})` : ""}`;
    const pendingDfsRnUpdatesTitle = `Pending DFS/RN Updates ${dashboardData?.referralsPendingDfsRnUpdates?.length ? `(${dashboardData.referralsPendingDfsRnUpdates.length})` : ""}`;
    const pendingHbovTitle = `Pending HBOV ${dashboardData?.referralsPendingHbov?.length ? `(${dashboardData.referralsPendingHbov.length})` : ""}`;
    const activeCallBackTitle = `Active Call Backs ${dashboardData?.activeCallBacks?.length ? `(${dashboardData.activeCallBacks.length})` : ""}`;
    const qaCallBackTitle = `Call Backs Pending Follow-Up ${dashboardData?.qaCallBacks?.length ? `(${dashboardData.qaCallBacks.length})` : ""}`;
    const referralImportsTitle = `Pushed from iTransplant ${dashboardData?.referralImports?.length ? `(${dashboardData.referralImports.length})` : ""}`;

    const [showImportModal, setShowImportModal] = useState(false);
    const [referralImport, setReferralImport] = useState<ReferralImport>();

    if (isLoading || createMutation.isLoading) return <LoadingSpinner />;

    return (
        <>
            {hasPermission(Claims.CreateCase) && (
                <Button className="mb-2" variant="outline-info" onClick={() => createMutation.mutate()}>
                    <Icon icon={fas.faPlus} /> New Referral
                </Button>
            )}

            <Row className="gx-3">
                <Col xl={7} md={12}>
                    <FormCard title={newReferralTitle} collapsible dark className="mb-3 bg-primary">
                        <Table hover className="referral-dashboard-table">
                            <tbody>
                                {dashboardData?.newReferrals.map((c) => (
                                    <ReferralRow
                                        key={c.id}
                                        case={c}
                                        onClick={() =>
                                            navigate(
                                                `/lists/${Lists.ReferralsNew}/cases/${c.id}/referral/${c.referral?.id}`
                                            )
                                        }
                                    />
                                ))}
                                {!dashboardData?.newReferrals.length && "(none)"}
                            </tbody>
                        </Table>
                    </FormCard>

                    <FormCard title={pendingDfsRnUpdatesTitle} collapsible dark className="mb-3 bg-primary">
                        <Table hover className="referral-dashboard-table">
                            <tbody>
                                {dashboardData?.referralsPendingDfsRnUpdates.map((c) => (
                                    <ReferralRow
                                        key={c.id}
                                        case={c}
                                        onClick={() =>
                                            navigate(
                                                `/lists/${Lists.ReferralsPendingDfsRn}/cases/${c.id}/referral/${c.referral?.id}`
                                            )
                                        }
                                    />
                                ))}
                                {!dashboardData?.referralsPendingDfsRnUpdates.length && "(none)"}
                            </tbody>
                        </Table>
                    </FormCard>

                    <FormCard title={pendingHbovTitle} collapsible dark className="mb-3 bg-primary">
                        <Table hover className="referral-dashboard-table">
                            <tbody>
                                {dashboardData?.referralsPendingHbov.map((c) => (
                                    <ReferralHbovRow
                                        key={c.id}
                                        case={c}
                                        onClick={() =>
                                            navigate(
                                                `/lists/${Lists.ReferralsHbov}/cases/${c.id}/referral/${c.referral?.id}`
                                            )
                                        }
                                    />
                                ))}
                                {!dashboardData?.referralsPendingHbov.length && "(none)"}
                            </tbody>
                        </Table>
                    </FormCard>
                </Col>

                <Col xl={5} md={12}>
                    <FormCard title={activeCallBackTitle} collapsible dark className="mb-3 bg-primary">
                        <Table hover className="referral-dashboard-table">
                            <tbody>
                                {dashboardData?.activeCallBacks.map((c) => (
                                    <CallBackRow
                                        key={c.id}
                                        callBack={c}
                                        onClick={() => handleShowCallBackModal(c.id)}
                                    />
                                ))}
                                {!dashboardData?.activeCallBacks.length && "(none)"}
                            </tbody>
                        </Table>
                    </FormCard>

                    <FormCard title={qaCallBackTitle} collapsible dark className="mb-3 bg-primary">
                        <Table hover className="referral-dashboard-table">
                            <tbody>
                                {dashboardData?.qaCallBacks.map((c) => (
                                    <CallBackRow
                                        key={c.id}
                                        callBack={c}
                                        onClick={() => handleShowCallBackModal(c.id)}
                                    />
                                ))}
                                {!dashboardData?.qaCallBacks.length && "(none)"}
                            </tbody>
                        </Table>
                    </FormCard>

                    <FormCard title={referralImportsTitle} collapsible dark className="mb-3 bg-primary">
                        <Table hover className="referral-dashboard-table">
                            <tbody>
                                {dashboardData?.referralImports?.map((r) => (
                                    <ReferralImportRow
                                        key={r.id}
                                        referralImport={r}
                                        onClick={() => {
                                            setReferralImport(r);
                                            setShowImportModal(true);
                                        }}
                                    />
                                ))}
                                {!dashboardData?.referralImports?.length && "(none)"}
                            </tbody>
                        </Table>
                    </FormCard>
                </Col>
            </Row>
            {referralImport && (
                <ReferralImportModal
                    referralImport={referralImport}
                    show={showImportModal}
                    onError={(error) => errorToast(error)}
                    onHide={() => {
                        setShowImportModal(false);
                        setReferralImport(undefined);
                    }}
                />
            )}
        </>
    );
}
